import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { createClient } from '../prismic/client'
import type { Content } from '@prismicio/client'
import { RichTextMapper } from '../prismic/richtext'
import { SuppotedLocales } from '../i18n'
import { useLocalization } from './localization-context'

interface PrismicBannersContextValue {
  banners: Content.BannersDocument[]
  loading: boolean
  dismissBanner?: (bannerId: string) => void
  error: Error | null
}

interface PrismicBannersProviderProps {
  children: React.ReactNode
  showAll?: boolean
  appId?: string
}

const PrismicBannersContext = createContext<PrismicBannersContextValue>({
  banners: [],
  loading: false,
  error: null,
})

export function Banners(props: Content.BannersDocument & { onDismiss: () => void }) {
  const style: React.CSSProperties = {
    backgroundColor: props.data.background_color ?? undefined,
    color: props.data?.text_color ?? undefined,
  }

  const className = props.data.background_color ? '' : 'bg-muted'

  return (
    <div className={`${className} relative w-full rounded-xl px-6 py-2`} style={style}>
      <RichTextMapper field={props.data.title} />
      <RichTextMapper field={props.data.body} />
      <button
        onClick={props.onDismiss}
        className="absolute right-2 top-2 flex items-center justify-center transition-colors"
        aria-label="Dismiss banner"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
  )
}

export function PrismicBannersProvider({ children, showAll, appId }: PrismicBannersProviderProps) {
  const { locale } = useLocalization()
  const [banners, setBanners] = useState<Content.BannersDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [dismissedBanners, setDismissedBanners] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true)
        const client = createClient()
        const response = await client.getAllByType('banners', { lang: locale || SuppotedLocales.EN_GB })
        setBanners(response)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch banners'))
        console.error('Error fetching banners:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchBanners()
  }, [])

  const handleDismiss = useCallback((bannerId: string) => {
    setDismissedBanners((prev) => {
      const updated = new Set(prev).add(bannerId)
      return updated
    })
  }, [])

  // 1. Show only active banners
  const activeBanners = banners.filter((b) => b.data.active)

  // 2. If targeting specific app id, show only those
  const appFilteredBanners = appId
    ? activeBanners.filter((b) => b.data.targets.some((target) => target.app_id === appId))
    : activeBanners

  // 3. If showAll, show all filtered banners; otherwise show only the most recent one
  const finalBanners = showAll ? appFilteredBanners : appFilteredBanners.slice(0, 1)

  // Filter out dismissed banners
  const visibleBanners = finalBanners.filter((b) => {
    return !dismissedBanners.has(b.id)
  })

  return (
    <PrismicBannersContext.Provider value={{ banners: visibleBanners, loading, error, dismissBanner: handleDismiss }}>
      {children}
    </PrismicBannersContext.Provider>
  )
}

export function usePrismicBanners() {
  const context = useContext(PrismicBannersContext)
  if (context === undefined) {
    throw new Error('usePrismicBanners must be used within a PrismicBannersProvider')
  }
  return context
}
